<template>
  <v-row class="bbr-import-recipe">
    <v-col cols="12">
      <div class="d-flex justify-space-between align-center">
        <v-btn
          class="bg-primary-gradient primary"
          @click="openImportDialog"
          depressed
        >
          <v-icon class="mr-2">{{ icons.add }}</v-icon>
          Import Recipes
        </v-btn>
      </div>
    </v-col>

    <v-col cols="12">
      <strong v-if="hasJobData">Results found: {{ jobMeta.total }}</strong>

      <v-card outlined class="px-4 py-4">
        <v-simple-table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Google Sheet ID</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Created At</th>
              <th>Completed At</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody
            v-infinite-scroll="loadMore"
            :infinite-scroll-disabled="disableListScroll"
          >
            <tr v-if="jobs.length === 0 && !loading">
              <td colspan="7" class="text-center headline grey--text py-12">
                NO DATA
              </td>
            </tr>

            <tr v-for="item in jobs" :key="item.id">
              <td>{{ item.id }}</td>
              <td>
                <a
                  :href="
                    `https://docs.google.com/spreadsheets/d/${item.source_google_sheet_id}`
                  "
                  target="_blank"
                  class="text-decoration-none"
                >
                  {{ item.source_google_sheet_id }}
                </a>
              </td>
              <td>
                <v-chip :color="getStatusColor(item.status)" small dark>
                  {{ capitalize(item.status) }}
                </v-chip>
              </td>
              <td>
                <div v-if="item.total_rows" style="min-width: 150px">
                  <v-progress-linear
                    :value="getProgressPercentage(item)"
                    :color="getProgressColor(item)"
                    height="20"
                    rounded
                  >
                    <template v-slot:default>
                      <small>
                        {{ item.success_count }} / {{ item.total_rows }}
                      </small>
                    </template>
                  </v-progress-linear>
                </div>
                <span v-else>-</span>
              </td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td>
                {{ item.completed_at ? formatDate(item.completed_at) : '-' }}
              </td>
              <td class="text-center">
                <v-btn
                  icon
                  small
                  @click="refreshJobStatus(item)"
                  :loading="refreshingJobIds.includes(item.id)"
                  :disabled="refreshingJobIds.includes(item.id)"
                >
                  <v-icon small>{{ icons.refresh }}</v-icon>
                </v-btn>
                <v-btn icon small @click="viewJobDetails(item)">
                  <v-icon small>{{ icons.eye }}</v-icon>
                </v-btn>
              </td>
            </tr>

            <tr v-if="loading">
              <td colspan="7" class="text-center py-4">
                <v-progress-circular indeterminate color="primary" />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </v-col>

    <!-- Import Dialog -->
    <import-recipe-dialog ref="importDialog" @submit="submitImport" />

    <!-- Toast Notification -->
    <toast ref="toast" />
  </v-row>
</template>

<script>
import { mdiPlus, mdiEye, mdiRefresh } from '@mdi/js'
import moment from 'moment'
import infiniteScroll from 'vue-infinite-scroll'
import ImportRecipeDialog from '../components/ImportRecipeDialog'
import Toast from '@/components/elements/Toast'

export default {
  name: 'ImportRecipe',

  components: {
    ImportRecipeDialog,
    Toast,
  },

  directives: {
    infiniteScroll,
  },

  data() {
    return {
      loading: false,
      jobs: [],
      jobMeta: {
        total: 0,
        current_page: 0,
        last_page: 1,
      },
      refreshingJobIds: [],
      icons: {
        add: mdiPlus,
        eye: mdiEye,
        refresh: mdiRefresh,
      },
    }
  },

  computed: {
    hasJobData() {
      return !!this.jobs.length
    },

    disableListScroll() {
      return this.loading || this.jobMeta.current_page >= this.jobMeta.last_page
    },
  },

  created() {
    this.fetchJobs()
  },

  methods: {
    openImportDialog() {
      this.$refs.importDialog.open()
    },

    async submitImport(form) {
      form.$busy = true
      form.$clearErrors()

      try {
        // Extract sheet ID from URL if needed
        let sheetId = form.google_sheet_id
        const urlMatch = sheetId.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
        if (urlMatch) {
          sheetId = urlMatch[1]
        }

        const { data } = await this.$api.post('foods/import-recipes', {
          google_sheet_id: sheetId,
          category_ids: form.category_ids,
          use_category_column: form.use_category_column,
          reset_ingredients: form.reset_ingredients,
          reset_booster_directions: form.reset_booster_directions,
        })

        this.$refs.toast.open({
          color: 'success',
          message: 'Import job created successfully!',
        })

        this.$refs.importDialog.close()

        // Navigate to the job detail page
        const job = data.data || data
        this.$router.push({
          name: 'import.recipe.detail',
          params: { id: job.id },
        })
      } catch (error) {
        if (error.response && error.response.status === 422) {
          form.$setErrors(error.response.data.errors)
        }

        this.$refs.toast.open({
          color: 'error',
          message:
            error.response?.data?.message || 'Failed to create import job',
        })
      } finally {
        form.$busy = false
      }
    },

    async fetchJobs(page = 1) {
      if (this.loading) return

      this.loading = true
      try {
        const { data } = await this.$api.get('foods/import-jobs', {
          params: { page },
        })

        // Append new jobs to existing list for infinite scroll
        if (page === 1) {
          this.jobs = data.data || []
        } else {
          this.jobs = [...this.jobs, ...(data.data || [])]
        }

        // Update pagination metadata
        this.jobMeta = {
          total: data.meta?.total || 0,
          current_page: data.meta?.current_page || page,
          last_page: data.meta?.last_page || 1,
        }
      } catch (error) {
        this.$refs.toast.open({
          color: 'error',
          message: 'Failed to fetch import jobs',
        })
      } finally {
        this.loading = false
      }
    },

    loadMore() {
      if (this.jobMeta.current_page < this.jobMeta.last_page) {
        this.fetchJobs(this.jobMeta.current_page + 1)
      }
    },

    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        processing: 'blue',
        completed: 'green',
        failed: 'red',
        cancelled: 'grey',
      }
      return colors[status] || 'grey'
    },

    getProgressPercentage(item) {
      if (!item.total_rows) return 0
      return (item.success_count / item.total_rows) * 100
    },

    getProgressColor(item) {
      if (item.status === 'completed') {
        return 'success'
      }
      if (item.status === 'failed') {
        return 'error'
      }
      return 'primary'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('MMM DD, YYYY HH:mm')
    },

    viewJobDetails(item) {
      this.$router.push({
        name: 'import.recipe.detail',
        params: { id: item.id },
      })
    },

    async refreshJobStatus(item) {
      this.refreshingJobIds.push(item.id)
      try {
        const { data } = await this.$api.get(`foods/import-jobs/${item.id}`)
        const updatedJob = data.data || data

        // Update the job in the list
        const index = this.jobs.findIndex((j) => j.id === item.id)
        if (index !== -1) {
          this.$set(this.jobs, index, updatedJob)
        }
      } catch (error) {
        this.$refs.toast.open({
          color: 'error',
          message: 'Failed to refresh job status',
        })
      } finally {
        this.refreshingJobIds = this.refreshingJobIds.filter(
          (id) => id !== item.id
        )
      }
    },
  },
}
</script>
